import {Component, Input, Output, EventEmitter, ContentChild,
	TemplateRef, ViewContainerRef} from 'angular2/core';
import {CORE_DIRECTIVES} from 'angular2/common';
import {Subscription} from 'rxjs';
import {PaginationService} from './pagination-service';

export interface IPage {
	label: string;
	value: any;
}

const DEFAULT_TEMPLATE = `
	<div class="btn-toolbar" *ngIf="displayDefaultTemplate()">
  		<div class="btn-group">
  		<button class="btn" [disabled]="isFirstPage()" (click)="setCurrent(1)">First</button>
  		<button class="btn" [disabled]="isFirstPage()" (click)="setCurrent(getCurrent() - 1)"><</button>
  		<button	*ngFor="#page of pages"
  			[class.btn-raised]="getCurrent() === page.value" class="btn" 
  			(click)="setCurrent(page.value)">{{ page.label }} 
  		</button>
  		<button class="btn" [disabled]="isLastPage()" (click)="setCurrent(getCurrent() + 1)">></button>
  		<button class="btn" [disabled]="isLastPage()" (click)="setLastPage()">Last</button>
  		</div>
  	</div>
`;

function getTemplate(): string {
	return PaginationService.template || DEFAULT_TEMPLATE;
}

@Component({
	selector: 'pagination',
	template: getTemplate(),
	directives: [CORE_DIRECTIVES]
})
export class PaginationCmp {
	@Input() id: string;
	@Input() maxSize: number = 7;
	@Input() autoHide: boolean = false;

	@Output() pageChange: EventEmitter<number> = new EventEmitter();

	@ContentChild(TemplateRef) customTemplate;

	public pages: IPage[] = [];
	private changeSub: Subscription<string>;

	constructor(private service: PaginationService,
		private viewContainer: ViewContainerRef) {
		this.changeSub = this.service.change
			.subscribe(id => {
				if (this.id === id) {
					this.updatePages();
				}
			});
	}

	public displayDefaultTemplate(): boolean {
		if (this.customTemplate !== null) {
			return false;
		}
		return !this.autoHide || 1 < this.pages.length;
	}

	public getCurrent(): number {
		return this.service.getCurrentPage(this.id);
	}

	public setCurrent(page: number) {
		this.service.setCurrentPage(this.id, page);
		this.pageChange.emit(this.service.getCurrentPage(this.id));
	}

	public isFirstPage() {
		return this.getCurrent() === 1;
	}

	public isLastPage() {
		let inst = this.service.getInstance(this.id);
        return Math.ceil(inst.totalItems / inst.itemsPerPage) === inst.currentPage;
	}

	public setLastPage() {
		let inst = this.service.getInstance(this.id);
        let lastPage = Math.ceil(inst.totalItems / inst.itemsPerPage);
		this.service.setCurrentPage(this.id, lastPage);
		this.pageChange.emit(this.service.getCurrentPage(this.id));
	}

	private ngOnInit() {
		if (this.id === undefined) {
			this.id = this.service.defaultId;
		}
	}

	private ngAfterContentInit() {
		if (this.customTemplate !== null) {
			this.viewContainer.createEmbeddedView(this.customTemplate);
		}
	}

	private ngOnChanges() {
		this.updatePages();
	}

	private ngOnDestroy() {
		this.changeSub.unsubscribe();
	}

	private updatePages() {
		let inst = this.service.getInstance(this.id);
		this.pages = this.createPageArray(inst.currentPage, inst.itemsPerPage,
			inst.totalItems, this.maxSize);
	}

	private createPageArray(currentPage: number, itemsPerPage: number,
							 totalItems: number, paginationRange: number): IPage[] {
		paginationRange = +paginationRange;
		let pages = [];
		const totalPages = Math.ceil(totalItems / itemsPerPage);
		const halfWay = Math.ceil(paginationRange / 2);

		const isStart = currentPage <= halfWay;
		const isEnd = totalPages - halfWay < currentPage;
		const isMiddle = !isStart && !isEnd;

		let ellipsesNeeded = paginationRange < totalPages;
		let i = 1;

		while (i <= totalPages && i <= paginationRange) {
			let label;
			let pageNumber = this.calculatePageNumber(i, currentPage, paginationRange, totalPages);
			let openingEllipsesNeeded = (i === 2 && (isMiddle || isEnd));
			let closingEllipsedNeeded = (i === paginationRange - 1 && (isMiddle || isStart));
			if (ellipsesNeeded && (openingEllipsesNeeded || closingEllipsedNeeded)) {
				label = '...';
			} else {
				label = pageNumber;
			}
			pages.push({
				label: label,
				value: pageNumber
			});
			i++;
		}
		return pages;
	}

	private calculatePageNumber(i: number, currentPage: number,
								paginationRange: number, totalPages: number): number {
		let halfWay = Math.ceil(paginationRange / 2);
        if (i === paginationRange) {
            return totalPages;
        } else if (i === 1) {
            return i;
        } else if (paginationRange < totalPages) {
            if (totalPages - halfWay < currentPage) {
                return totalPages - paginationRange + i;
            } else if (halfWay < currentPage) {
                return currentPage - halfWay + i;
            } else {
                return i;
            }
        } else {
            return i;
        }
    }
}




/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./src/pagination/pagination.ts
 **/